import { ActivityEntityName, EntityViewDisplay, Lead, SalesSecurityRight, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { leadCardSummary } from './entity.card.renderer/lead.card.summary';
import { leadStageTag } from './presentation/lead.stage.colour';
import { navigateToLeadManage } from './services/lead.board.open';

// Lead entity manifest: board displays, Creator/Manage surfaces, and the activity relation.
export const leadManifest = registerManifest<Lead>({
  entity: ActivityEntityName.Lead,
  slug: 'lead',

  routes: {
    root: '/sales/leads-pro',
  },

  board: {
    // Card + Table only — no Kanban (no usable Lead kanban view/card config).
    // No finance document panel. Row activation opens Manage in the shell inspector; the surface
    // applies Manage-right and stage readonly policy.
    supportedDisplays: [EntityViewDisplay.Card, EntityViewDisplay.Table],
    defaults: {
      header: {
        title: 'Leads',
        showSearch: true,
        showPaginator: true,
      },
      itemInteraction: {
        navigate: navigateToLeadManage,
      },
    },
  },

  presentation: {
    card: {
      component: () => import('./entity.card.renderer/lead.entity.card.renderer.component').then(module => module.LeadEntityCardRendererComponent),
      resolution: 'snapshot',
      summarize: leadCardSummary,
    },
    tags: {
      stage: leadStageTag,
    },
  },

  // Agreed exports: leadCreatorSurface / leadManageSurface (Create/Manage streams own the files).
  surfaces: {
    [SurfaceType.Creator]: () => import('./surface/creator/lead.creator.surface').then(module => module.leadCreatorSurface),
    [SurfaceType.Manage]: () => import('./surface/manage/lead.manage.surface').then(module => module.leadManageSurface),
  },

  operations: {
    create: {
      surface: SurfaceType.Creator,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(SalesSecurityRight.ManageLeads),
    },
    preview: {
      surface: SurfaceType.Manage,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(SalesSecurityRight.CanAccessLeads),
    },
    // Access mounts the workspace; Manage rights and record stage remain surface readonly policy.
    edit: {
      surface: SurfaceType.Manage,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(SalesSecurityRight.CanAccessLeads),
    },
  },

  relations: {
    [ActivityEntityName.Activity]: () => import('./relations/lead.activities.relation').then(module => module.leadActivitiesRelationBuilder),
  },
});
